/* ============================================================
 * Caps Header — drawer + sub-panels + compat drill-down
 *
 * Behaviors:
 *  - Hamburger toggles the drawer (with body scroll lock)
 *  - Desktop: hover on category opens its sub-panel (with delay to avoid accidental switches)
 *  - Desktop: hover on drilldown card opens compat panel
 *  - Mobile: click on category slides to drill-down level 1
 *  - Click on a card with [data-drilldown-handle] opens compat panel (drill-down 2)
 *  - Back arrows (mobile) close panels one level at a time
 *  - ESC + overlay click + close button close everything
 *  - Cart icon: relies on Prestige theme JS (data-action="open-drawer")
 * ============================================================ */

(function () {
  'use strict';

  var DESKTOP_MQ = window.matchMedia('(min-width: 1035px)');
  var HOVER_DELAY = 150; // ms before hover triggers sub-panel switch

  var hoverTimer = null;

  function init() {
    var menu = document.querySelector('[data-caps-menu]');
    if (!menu) return;

    var trigger = document.querySelector('[data-caps-menu-trigger]');
    var closers = document.querySelectorAll('[data-caps-menu-close]');
    var categories = menu.querySelectorAll('[data-category-handle]');
    var categoryLinks = menu.querySelectorAll('.caps-menu__category--link');
    var subPanels = menu.querySelectorAll('[data-sub-panel-handle]');
    var compatPanels = menu.querySelectorAll('[data-compat-handle]');
    var backBtns = menu.querySelectorAll('[data-caps-back]');
    var backCompatBtns = menu.querySelectorAll('[data-caps-back-compat]');
    // Locale picker is now a disclosure custom element (see caps-locale-selector.liquid).
    // Keeping the legacy hook for backward-compat with old markup.
    var locale = document.querySelector('[data-caps-locale-select]');

    /* ─── Drawer toggle ─── */
    function openMenu() {
      menu.setAttribute('aria-hidden', 'false');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('caps-menu-open');
    }

    function closeMenu() {
      menu.setAttribute('aria-hidden', 'true');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('caps-menu-open');
      clearTimeout(hoverTimer);
      closeAllSubPanels();
      closeAllCompatPanels();
    }

    if (trigger) {
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        if (menu.getAttribute('aria-hidden') === 'false') {
          closeMenu();
        } else {
          openMenu();
        }
      });
    }

    closers.forEach(function (el) {
      el.addEventListener('click', function (e) {
        e.preventDefault();
        closeMenu();
      });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (menu.getAttribute('aria-hidden') === 'false') {
        closeMenu();
      }
    });

    /* ─── Sub-panels ─── */
    function openSubPanel(handle) {
      closeAllCompatPanels();
      subPanels.forEach(function (panel) {
        if (panel.getAttribute('data-sub-panel-handle') === handle) {
          panel.classList.add('caps-menu__sub-panel--open');
          panel.setAttribute('aria-hidden', 'false');
        } else {
          panel.classList.remove('caps-menu__sub-panel--open');
          panel.setAttribute('aria-hidden', 'true');
        }
      });
      categories.forEach(function (cat) {
        if (cat.getAttribute('data-category-handle') === handle) {
          cat.classList.add('caps-menu__category--active');
          cat.setAttribute('aria-expanded', 'true');
        } else {
          cat.classList.remove('caps-menu__category--active');
          cat.setAttribute('aria-expanded', 'false');
        }
      });
    }

    function closeAllSubPanels() {
      subPanels.forEach(function (panel) {
        panel.classList.remove('caps-menu__sub-panel--open');
        panel.setAttribute('aria-hidden', 'true');
      });
      categories.forEach(function (cat) {
        cat.classList.remove('caps-menu__category--active');
        cat.setAttribute('aria-expanded', 'false');
      });
    }

    /* ─── Compat panels ─── */
    function openCompatPanel(handle) {
      compatPanels.forEach(function (panel) {
        if (panel.getAttribute('data-compat-handle') === handle) {
          panel.classList.add('caps-menu__compat-panel--open');
          panel.setAttribute('aria-hidden', 'false');
        } else {
          panel.classList.remove('caps-menu__compat-panel--open');
          panel.setAttribute('aria-hidden', 'true');
        }
      });
      // Mark the active drilldown card
      drilldownCards.forEach(function (card) {
        if (card.getAttribute('data-drilldown-handle') === handle) {
          card.classList.add('caps-menu__card--drilldown-active');
        } else {
          card.classList.remove('caps-menu__card--drilldown-active');
        }
      });
    }

    function closeAllCompatPanels() {
      compatPanels.forEach(function (panel) {
        panel.classList.remove('caps-menu__compat-panel--open');
        panel.setAttribute('aria-hidden', 'true');
      });
      drilldownCards.forEach(function (card) {
        card.classList.remove('caps-menu__card--drilldown-active');
      });
    }

    /* ─── Category interactions ─── */
    categories.forEach(function (cat) {
      var handle = cat.getAttribute('data-category-handle');

      // Mobile: click → drill-down
      cat.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        openSubPanel(handle);
      });

      // Desktop: hover with delay → avoids accidental switches when mouse passes fast
      cat.addEventListener('mouseenter', function () {
        if (!DESKTOP_MQ.matches) return;
        clearTimeout(hoverTimer);
        hoverTimer = setTimeout(function () {
          openSubPanel(handle);
        }, HOVER_DELAY);
      });

      cat.addEventListener('mouseleave', function () {
        if (!DESKTOP_MQ.matches) return;
        clearTimeout(hoverTimer);
      });
    });

    /* ─── Category links without children — hover closes all panels ─── */
    categoryLinks.forEach(function (link) {
      link.addEventListener('mouseenter', function () {
        if (!DESKTOP_MQ.matches) return;
        clearTimeout(hoverTimer);
        hoverTimer = setTimeout(function () {
          closeAllSubPanels();
          closeAllCompatPanels();
        }, HOVER_DELAY);
      });

      link.addEventListener('mouseleave', function () {
        if (!DESKTOP_MQ.matches) return;
        clearTimeout(hoverTimer);
      });
    });

    /* ─── Drill-down cards — click (mobile) + hover (desktop) ─── */
    var drilldownCards = menu.querySelectorAll('[data-drilldown-handle]');
    drilldownCards.forEach(function (card) {
      // Click: works on both mobile and desktop
      card.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var handle = card.getAttribute('data-drilldown-handle');
        openCompatPanel(handle);
      });

      // Desktop: hover opens compat panel (with same delay pattern)
      card.addEventListener('mouseenter', function () {
        if (!DESKTOP_MQ.matches) return;
        var handle = card.getAttribute('data-drilldown-handle');
        clearTimeout(hoverTimer);
        hoverTimer = setTimeout(function () {
          openCompatPanel(handle);
        }, HOVER_DELAY);
      });

      card.addEventListener('mouseleave', function () {
        if (!DESKTOP_MQ.matches) return;
        clearTimeout(hoverTimer);
      });
    });

    /* ─── Back buttons (mobile) ─── */
    backBtns.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        closeAllCompatPanels();
        closeAllSubPanels();
      });
    });

    backCompatBtns.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        closeAllCompatPanels();
      });
    });

    /* ─── Desktop: close sub-panels when leaving the menu area ─── */
    if (menu) {
      menu.addEventListener('mouseleave', function () {
        if (DESKTOP_MQ.matches) {
          clearTimeout(hoverTimer);
          closeAllSubPanels();
          closeAllCompatPanels();
        }
      });
    }

    /* ─── Search bar ─── */
    var searchBar = document.querySelector('[data-caps-search]');
    var searchTrigger = document.querySelector('[data-caps-search-trigger]');
    var searchClose = document.querySelector('[data-caps-search-close]');
    var searchInput = document.querySelector('[data-caps-search-input]');

    var header = document.querySelector('.caps-header');

    function openSearch() {
      if (!searchBar) return;
      searchBar.setAttribute('aria-hidden', 'false');
      if (searchTrigger) searchTrigger.setAttribute('aria-expanded', 'true');
      if (header) header.classList.add('caps-header--search-open');
      if (searchInput) searchInput.focus();
    }

    function closeSearch() {
      if (!searchBar) return;
      searchBar.setAttribute('aria-hidden', 'true');
      if (searchTrigger) searchTrigger.setAttribute('aria-expanded', 'false');
      if (header) header.classList.remove('caps-header--search-open');
      if (searchInput) searchInput.value = '';
    }

    if (searchTrigger) {
      searchTrigger.addEventListener('click', function (e) {
        e.preventDefault();
        if (searchBar && searchBar.getAttribute('aria-hidden') === 'false') {
          closeSearch();
        } else {
          openSearch();
        }
      });
    }

    if (searchClose) {
      searchClose.addEventListener('click', function (e) {
        e.preventDefault();
        closeSearch();
      });
    }

    // ESC also closes search
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && searchBar && searchBar.getAttribute('aria-hidden') === 'false') {
        closeSearch();
      }
    });

    // Click outside closes search
    document.addEventListener('click', function (e) {
      if (!searchBar || searchBar.getAttribute('aria-hidden') === 'true') return;
      if (!searchBar.contains(e.target) && e.target !== searchTrigger && !searchTrigger.contains(e.target)) {
        closeSearch();
      }
    });

    /* ─── Locale selector auto-submit ─── */
    if (locale) {
      locale.addEventListener('change', function () {
        var form = locale.closest('form');
        if (form) form.submit();
      });
    }

    /* ─── Reset state when switching breakpoints ─── */
    DESKTOP_MQ.addEventListener('change', function () {
      clearTimeout(hoverTimer);
      closeAllSubPanels();
      closeAllCompatPanels();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
